import { useMemo, useRef, useState } from "react";
import { Alert, Button, DialogActions, DialogContent, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import LoadingButton from "@mui/lab/LoadingButton";
import { v4 as randomUuid } from "uuid";
import { useGetList, useGetOne } from "react-admin";
import type { ServerClient } from "../api/server.api";
import { runSafelyWithServerClient } from "../api/server.api";
import type { SignatureRequestType } from "../model/SignatureRequest";
import { PdfViewer, type PdfViewerHandle } from "../misc/PdfViewer";
import type { Student } from "../providers/studentsProvider";
import type { Instructor } from "../providers/instructorsProvider";
import type { Branch } from "../providers/branchesProvider";

export interface PdfPreview {
  dataSnapshot: string;
  pdf: Blob;
}

export async function createPdfPreview(props: {
  serverClient: ServerClient;
  createPdf: () => Promise<unknown>;
}): Promise<PdfPreview> {
  const { serverClient, createPdf } = props;
  let dataSnapshot: undefined | string;
  serverClient.use({
    // eslint-disable-next-line @typescript-eslint/require-await
    request: async (_, config) => ({ ...config, responseType: "blob" }),
    // eslint-disable-next-line @typescript-eslint/require-await
    response: async (_api, _config, response) => {
      const contentDispositionHeader = response.headers["content-disposition"];
      if (!contentDispositionHeader) {
        throw new Error("No Content-Disposition header in response");
      }
      const match = /filename="([^.]+).pdf"/.exec(contentDispositionHeader);
      if (!match) {
        throw new Error(`Unexpected Content-Disposition header: ${JSON.stringify(contentDispositionHeader)}`);
      }
      dataSnapshot = match[1];
      return response;
    },
  });
  const pdf = (await createPdf()) as Blob;
  if (!dataSnapshot) {
    throw new Error("dataSnapshot not set");
  }
  return { dataSnapshot, pdf };
}

export function PdfPreviewDialogContent(props: {
  student: Student;
  preview: PdfPreview;
  documentName: string;
  documentNameWithArticle: string;
  signatureRequestType: SignatureRequestType;
  closeDialog: () => void;
}) {
  const { student, preview, documentName, documentNameWithArticle, signatureRequestType, closeDialog } = props;
  const [creatingSignatureRequest, setCreatingSignatureRequest] = useState(false);
  const pdfViewerRef = useRef<PdfViewerHandle>(null);

  const { data: managers = [], isLoading: instructorsLoading } = useGetList<Instructor>("instructors", {
    filter: { drivingSchoolId: student.drivingSchoolId, isActive: true, isDrivingSchoolManager: true },
    pagination: { page: 1, perPage: 999 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: branch, isLoading: branchLoading } = useGetOne<Branch>(
    "branches",
    { id: student.branchId! },
    { enabled: Boolean(student.branchId) },
  );

  const rankedManagerIds = useMemo(() => {
    return [...managers]
      .sort((a, b) => {
        const countDiff = b.studentIds.length - a.studentIds?.length;
        if (countDiff !== 0) return countDiff;
        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
      })
      .map((it) => it.id);
  }, [managers]);

  const defaultDrivingSchoolManagerId = useMemo(() => {
    const branchDefault = branch?.defaultDrivingSchoolManagerId;
    if (branchDefault && rankedManagerIds.includes(branchDefault)) {
      return branchDefault;
    }
    return rankedManagerIds[0];
  }, [branch, rankedManagerIds]);

  const [selectedDrivingSchoolManagerId, setSelectedDrivingSchoolManagerId] = useState<string | undefined>(undefined);
  const effectiveDrivingSchoolManagerId = selectedDrivingSchoolManagerId ?? defaultDrivingSchoolManagerId;

  const addToSignatureFolder = async () => {
    if (!effectiveDrivingSchoolManagerId) {
      throw new Error("Cannot create signature request without an assigned manager");
    }
    setCreatingSignatureRequest(true);
    await runSafelyWithServerClient(
      async (serverClient) => {
        await serverClient.createSignatureRequest({
          idempotencyKey: randomUuid(),
          type: signatureRequestType,
          studentId: student.id,
          assignedDrivingSchoolManagerId: effectiveDrivingSchoolManagerId,
          dataSnapshot: preview.dataSnapshot,
        });
      },
      {
        errorMessage: `Fehler beim Hinzufügen ${documentNameWithArticle} zur Unterschriftenmappe.`,
        successMessage: `${documentName} erfolgreich zur Unterschriftenmappe hinzugefügt.`,
        onSuccess: closeDialog,
      },
    );
    setCreatingSignatureRequest(false);
  };

  const showPicker = managers.length > 1;
  const noManagers = !instructorsLoading && managers.length === 0;

  // By default DialogContent has a padding-bottom: 30px and DialogActions padding-top: 30px,
  // but somehow the padding-bottom of DialogContent is not respected by the <iframe> element
  // showing the PDF when the browser window as a small height. To fix this we use
  // <DialogContent sx={{ paddingBottom: 0 }}> and<DialogActions sx={{ paddingTop: "30px" }}> below ...
  return (
    <>
      <DialogContent sx={{ paddingBottom: 0, overflowY: "hidden" }}>
        <PdfViewer ref={pdfViewerRef} pdf={preview.pdf} sx={{ width: "100%", height: "80vh" }} />
      </DialogContent>
      <DialogActions sx={{ paddingTop: "30px", gap: 1, flexWrap: "wrap" }}>
        {noManagers && (
          <Alert severity="warning" sx={{ width: "100%" }}>
            Diese Fahrschule hat keine:n Fahrschulleiter:in. {documentName} kann daher nicht zur Unterschriftenmappe
            hinzugefügt werden.
          </Alert>
        )}
        {showPicker && (
          <FormControl size="small" sx={{ minWidth: 260, marginRight: "auto" }}>
            <InputLabel id="signature-folder-manager-label">Unterschriftenmappe von</InputLabel>
            <Select
              labelId="signature-folder-manager-label"
              label="Unterschriftenmappe von"
              value={effectiveDrivingSchoolManagerId ?? ""}
              onChange={(event) => setSelectedDrivingSchoolManagerId(event.target.value)}
              disabled={creatingSignatureRequest}
            >
              {managers.map((manager) => (
                <MenuItem key={manager.id} value={manager.id}>
                  {manager.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={() => pdfViewerRef.current?.print()}>
          Drucken
        </Button>
        <LoadingButton
          variant="contained"
          loading={creatingSignatureRequest}
          disabled={instructorsLoading || branchLoading || noManagers || !effectiveDrivingSchoolManagerId}
          onClick={addToSignatureFolder}
        >
          Zur Unterschriftenmappe hinzufügen
        </LoadingButton>
      </DialogActions>
    </>
  );
}
